import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import { LoginFormData } from '../../types';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import LinkedInButton from '../../components/auth/LinkedInButton';
import GoogleButton from '../../components/auth/GoogleButton';
import IDCoderBrandLockup from '../../components/brand/IDCoderBrandLockup';
import { COURSE_IMAGES } from '../../config/courseImages';
import { heroPhotoScrim } from '../../content/heroThemes';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const mfaSchema = z.object({
  code: z.string().regex(/^\d{5}$/, 'Enter the 5-digit code from your email'),
});

type MfaFormData = { code: string };

const maskEmail = (email: string) => {
  const [local, domain] = email.split('@');
  if (!domain || !local) {
    return email;
  }
  return `${local.slice(0, 1)}***@${domain}`;
};

const Login: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const { login, verifyLoginMfa, resendLoginMfa } = useAuth();
  const location = useLocation();

  const { register, handleSubmit, getValues, formState: { errors } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const {
    register: registerMfa,
    handleSubmit: handleMfaSubmit,
    reset: resetMfa,
    formState: { errors: mfaErrors },
  } = useForm<MfaFormData>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (!mfaToken) {
      return;
    }
    resetMfa({ code: '' });
    const handle = window.setTimeout(() => resetMfa({ code: '' }), 80);
    return () => window.clearTimeout(handle);
  }, [mfaToken, resetMfa]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await login(data.email, data.password, location.state?.from);

      if (result.requiresMfa && result.mfaToken) {
        setPendingEmail(data.email);
        setMfaToken(result.mfaToken);
        return;
      }

      if (!result.authenticated) {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err: unknown) {
      const loginError = err as Error;
      setError(loginError.message || 'An error occurred during login. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const onMfaSubmit = async (data: MfaFormData) => {
    if (!mfaToken) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const success = await verifyLoginMfa(mfaToken, Number(data.code), location.state?.from);
      if (!success) {
        setError('Invalid code. Please try again.');
      }
    } catch (err: unknown) {
      const loginError = err as Error;
      setError(loginError.message || 'Could not verify the sign-in code.');
    } finally {
      setIsLoading(false);
    }
  };

  const onResend = async () => {
    if (!mfaToken) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await resendLoginMfa(mfaToken);
    } catch (err: unknown) {
      const loginError = err as Error;
      setError(loginError.message || 'Could not resend the code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc((100dvh-4.5rem)*0.9)]">
      <aside className="relative min-h-[12.6rem] sm:min-h-[16.2rem] lg:min-h-full overflow-hidden bg-slate-950">
        <img
          src={COURSE_IMAGES.loginPanel}
          alt="Engineers collaborating in a studio"
          className="absolute inset-0 h-full w-full object-cover object-[center_22%]"
        />
        <div className={heroPhotoScrim('account')} />
        <div className="relative z-10 flex h-full flex-col justify-end p-5 sm:p-6 lg:p-8 text-white">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="w-full max-w-md"
          >
            <div className="rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-sm px-3 py-2.5">
              <IDCoderBrandLockup size="md" variant="dark" className="w-full" />
            </div>
          </motion.div>
        </div>
      </aside>

      <section className="relative flex flex-col justify-center px-4 py-6 sm:px-8 lg:px-10">
        {isLoading && <LoadingOverlay />}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mx-auto w-full max-w-md surface-card p-5 sm:p-6"
        >
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {mfaToken ? 'Check your email' : 'Welcome back'}
          </h1>
          <p className="mt-1.5 text-sm text-gray-600">
            {mfaToken
              ? `Enter the 5-digit code we sent to ${maskEmail(pendingEmail || getValues('email'))}.`
              : 'Sign in to continue to IDCoder.'}
          </p>

          {location.state?.message && (
            <div className="mt-5 p-3 bg-warning-50 text-warning-700 text-sm rounded-md border border-warning-200">
              {location.state.message}
            </div>
          )}

          {error && (
            <div className="mt-5 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
              {error}
            </div>
          )}

          {!mfaToken && (
          <div className="mt-5 space-y-3">
            <LinkedInButton primary disabled={isLoading} onError={setError} />
            <GoogleButton primary disabled={isLoading} onError={setError} />
          </div>
          )}

          {!mfaToken && (
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
              or continue with email
            </span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          )}

          {mfaToken ? (
          <form key={mfaToken} onSubmit={handleMfaSubmit(onMfaSubmit)} className="mt-5 space-y-4" autoComplete="off">
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={5}
              label="Sign-in code"
              placeholder=""
              error={mfaErrors.code?.message}
              {...registerMfa('code')}
            />
            <Button type="submit" fullWidth isLoading={isLoading}>
              Verify and sign in
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                className="font-medium text-primary-600 hover:text-primary-500"
                onClick={() => {
                  setMfaToken(null);
                  setError(null);
                }}
              >
                Use a different account
              </button>
              <button
                type="button"
                className="font-medium text-primary-600 hover:text-primary-500"
                onClick={onResend}
                disabled={isLoading}
              >
                Resend code
              </button>
            </div>
          </form>
          ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
            <Input
              id="email"
              type="email"
              label="Email address"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                autoComplete="current-password"
                error={errors.password?.message}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute right-3 top-9 text-gray-500 focus:outline-none"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            <div className="flex items-center justify-end pb-4">
              <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                Forgot your password?
              </Link>
            </div>

            <Button type="submit" fullWidth isLoading={isLoading}>
              Sign in with email
            </Button>
          </form>
          )}

          <p className="mt-5 text-center text-sm text-gray-600">
            New to IDCoder?{' '}
            <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-500">
              Create an account
            </Link>
          </p>
        </motion.div>
      </section>
    </div>
  );
};

export default Login;
